"use client"

import { CheckIconSVG, CrossIconSVG } from "@/src/ui/AllSVG"
import { formatAmount } from "@/src/utils/formatters"
import { Button } from "@heroui/react"
import React from "react"

type FeatureValue = boolean | "included" | "dash"

interface FeatureRow {
    label: string
    essential: FeatureValue
    premier: FeatureValue
}

interface FeatureGroup {
    category: string
    features: FeatureRow[]
}

const FEATURE_GROUPS: FeatureGroup[] = [
    {
        category: "Core Features",
        features: [
            { label: "Online Store Access", essential: true, premier: true },
            { label: "New Arrivals & Curated Edits", essential: true, premier: true },
            // { label: "Style Guides & Lookbooks", essential: true, premier: true },
            { label: "Seasonal Sale Access", essential: true, premier: true },
        ],
    },
    {
        category: "Advanced Features",
        features: [
            { label: "Early Collection Access", essential: true, premier: true },
            { label: "Members-Only Pricing", essential: false, premier: true },
            { label: "Reduced Shipping Charges", essential: false, premier: true },
            // { label: "Personal Style Consultation", essential: "dash", premier: "included" },
            { label: "Priority Customer Care", essential: false, premier: true },
        ],
    },
    // {
    //     category: "Additional Benefits",
    //     features: [
    //         { label: "Exclusive Event Invitations", essential: "dash", premier: "included" },
    //         { label: "Priority Customer Care", essential: true, premier: true },
    //         { label: "Gift Wrapping & Premium Packaging", essential: true, premier: true },
    //         { label: "Loyalty Rewards Programme", essential: true, premier: true },
    //     ],
    // },
]

const PLANS = {
    essential: {
        description: "Free no commitment required. Great for browsing and discovering the VALLOTÉ world.",
    },
    premier: {
        description: "/ year the full VALLOTÉ experience with exclusive perks and early access.",
    },
}

function FeatureCell({ value }: { value: FeatureValue }) {
    if (value === true) {
        return (
            <div className="flex justify-center">
                <div className="bg-[#F4F6F7] h-5 w-5 text-body-color rounded-full flex items-center justify-center">
                    <CheckIconSVG />
                </div>
            </div>
        )
    }
    if (value === "included") {
        return <div className="text-center text-sm font-medium text-body-color">Included</div>
    }
    if (value === "dash") {
        return <div className="text-center text-sm text-body-color/40">—</div>
    }
    // value === false
    return (
        <div className="flex justify-center">
            <div className="bg-red-50 h-5 w-5 text-red-500 rounded-full flex items-center justify-center">
                <CrossIconSVG />
            </div>
        </div>
    )
}

interface PlanComparisonProps {
    membershipPrice: number
    membershipCurrency: string
}
export default function PlanComparison({ membershipPrice, membershipCurrency }: PlanComparisonProps) {
    const premierPriceDisplay = formatAmount(membershipPrice, membershipCurrency)
    return (
        <div className="custom_container">
            {/* ── Mobile header ── */}
            <div className="md:hidden flex flex-col gap-5 pb-6">
                {/* Billing label */}
                <div className="flex justify-center">
                    <div className="bg-body-color text-white rounded-full px-5 py-2 text-sm">
                        Yearly
                    </div>
                </div>

                {/* Plan cards stacked side-by-side */}
                <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-3 p-4 border border-muted-color rounded-xl">
                        <div>
                            <h3 className="text-base font-medium">Essential</h3>
                            <p className="text-xs text-body-color/50 font-light leading-relaxed mt-1">
                                {PLANS.essential.description}
                            </p>
                        </div>
                        {/* <Button className="global_btn rounded_full w-full font-normal! bg-[#F4F6F7]! text-body-color! text-xs!">
                            GET STARTED
                        </Button> */}
                    </div>
                    <div className="flex flex-col gap-3 p-4 border border-muted-color rounded-xl">
                        <div>
                            <h3 className="text-base font-medium">Premier</h3>
                            <p className="text-xs text-body-color/50 font-light leading-relaxed mt-1">
                                <span className="font-medium text-body-color">{premierPriceDisplay}</span>{" "}
                                {PLANS.premier.description}
                            </p>
                        </div>
                        {/* <Button className="global_btn rounded_full w-full font-normal! bg_primary text-xs!">
                            GET STARTED
                        </Button> */}
                    </div>
                </div>

                {/* Mobile column labels */}
                <div className="grid grid-cols-[2fr_1fr_1fr] gap-x-3 pb-1 border-b border-[#F0F0F0]">
                    <div />
                    <p className="text-center text-xs font-medium text-body-color/50 uppercase tracking-wide">Ess.</p>
                    <p className="text-center text-xs font-medium text-body-color/50 uppercase tracking-wide">Prem.</p>
                </div>
            </div>

            {/* ── Desktop header ── */}
            <div className="hidden md:grid grid-cols-[2fr_1.5fr_1.5fr] gap-x-6 items-start pb-6">
                {/* Left: label + yearly badge */}
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-body-color/50">Compare membership<br />tiers</p>
                    <div className="bg-body-color text-white rounded-full px-5 py-2 text-sm w-fit">
                        Yearly
                    </div>
                </div>

                {/* Essential header */}
                <div className="flex flex-col gap-4">
                    <div className="space-y-1.5">
                        <h3 className="text-2xl font-medium">Essential</h3>
                        <p className="text-sm text-body-color/60 font-light leading-relaxed">
                            {PLANS.essential.description}
                        </p>
                    </div>
                    {/* <Button className="global_btn rounded_full w-full font-normal! bg-[#F4F6F7]! text-body-color!">
                        GET STARTED NOW
                    </Button> */}
                </div>

                {/* Premier header */}
                <div className="flex flex-col gap-4">
                    <div className="space-y-1.5">
                        <h3 className="text-2xl font-medium">Premier</h3>
                        <p className="text-sm text-body-color/60 font-light leading-relaxed">
                            <span className="font-medium text-body-color">{premierPriceDisplay}</span>{" "}
                            {PLANS.premier.description}
                        </p>
                    </div>
                    {/* <Button className="global_btn rounded_full w-full font-normal! bg_primary">
                        GET STARTED NOW
                    </Button> */}
                </div>
            </div>

            {/* ── Feature groups (shared) ── */}
            <div className="space-y-6">
                {FEATURE_GROUPS.map((group) => (
                    <div key={group.category}>
                        <div className="py-3">
                            <h4 className="text-sm md:text-xl font-medium text-body-color">{group.category}</h4>
                        </div>
                        {group.features.map((feature) => (
                            <div
                                key={feature.label}
                                className="grid grid-cols-[2fr_1fr_1fr] md:grid-cols-[2fr_1.5fr_1.5fr] gap-x-3 md:gap-x-6 py-3 md:py-3.5 border-b border-[#F0F0F0] items-center"
                            >
                                <p className="text-xs md:text-sm font-light text-body-color/60">{feature.label}</p>
                                <FeatureCell value={feature.essential} />
                                <FeatureCell value={feature.premier} />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    )
}
